import React, { useContext, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from '../../../ui/toast';
import {
  BrainIcon,
  BriefcaseBusinessIcon,
  CheckIcon,
  CircleCheckIcon,
  ClipboardCheckIcon,
  HeartHandshakeIcon,
  SparklesIcon,
  UsersRoundIcon,
  XIcon,
} from '../../../icons/animated';
import { ADMIN_PAGES_KEYS, NS } from '../../../i18n/constants/keys';
import { createJobOpening } from '../../../utils/api_service/JobOpenings';
import { JobOpeningsContext } from '../../../utils/contexts/JobOpening';
import { RootState } from '../../../utils/redux_store/store';
import { SubscriptionDetails } from '../../../utils/types/admin_types';
import { JOB_OPENING_TYPE } from '../Constants';

const SPRING = {
  type: 'spring',
  stiffness: 400,
  damping: 30,
  mass: 0.8,
};

export interface ICreateJobOpeningDialog {
  open: boolean;
  onClose: () => void;
  onSuccessClose?: () => void;
}

interface ElementDefinition {
  title: JOB_OPENING_TYPE;
  label: string;
  icon: React.ReactNode;
  helpText: string;
}

export const CreateJobOpeningDialog: React.FC<ICreateJobOpeningDialog> = ({
  open,
  onClose,
  onSuccessClose,
}) => {
  const { t } = useTranslation(NS.adminPages);
  const history = useHistory();
  const { setJobOpeningElements, setCompletedJobOpeningDetails } =
    useContext(JobOpeningsContext);

  const { subscriptions } = useSelector(
    (state: RootState) => state.subscriptions,
  );

  const helpTextMap: Record<JOB_OPENING_TYPE, string> = {
    [JOB_OPENING_TYPE.JOB_POSTING]: t(
      ADMIN_PAGES_KEYS.jobOpenings.createDialog.helpText.jobPosting,
    ),
    [JOB_OPENING_TYPE.APPLICANT_ANALYSIS]: t(
      ADMIN_PAGES_KEYS.jobOpenings.createDialog.helpText.applicantAnalysis,
    ),
    [JOB_OPENING_TYPE.AUTO_SUITABILITY]: t(
      ADMIN_PAGES_KEYS.jobOpenings.createDialog.helpText.autoSuitability,
    ),
    [JOB_OPENING_TYPE.AUTO_OUTSTANDING]: t(
      ADMIN_PAGES_KEYS.jobOpenings.createDialog.helpText.autoOutstanding,
    ),
    [JOB_OPENING_TYPE.AUTO_ASSESMENTS]: t(
      ADMIN_PAGES_KEYS.jobOpenings.createDialog.helpText.autoAssessments,
    ),
    [JOB_OPENING_TYPE.SMART_INTERVIEWS]: t(
      ADMIN_PAGES_KEYS.jobOpenings.createDialog.helpText.smartInterviews,
    ),
    [JOB_OPENING_TYPE.CCA]: '',
    [JOB_OPENING_TYPE.JOB_OPENING_DETAILS]: '',
    [JOB_OPENING_TYPE.ANALITICS]: '',
    [JOB_OPENING_TYPE.PSYCHOMETRIC]: t(
      ADMIN_PAGES_KEYS.jobOpenings.createDialog.helpText.psychometric,
    ),
    [JOB_OPENING_TYPE.WELLNESS]: t(
      ADMIN_PAGES_KEYS.jobOpenings.createDialog.helpText.wellness,
    ),
  };

  const allElements: ElementDefinition[] = [
    {
      title: JOB_OPENING_TYPE.JOB_POSTING,
      label: 'JOB POSTING',
      icon: <BriefcaseBusinessIcon size={26} />,
      helpText: helpTextMap[JOB_OPENING_TYPE.JOB_POSTING],
    },
    {
      title: JOB_OPENING_TYPE.APPLICANT_ANALYSIS,
      label: 'APPLICANT ANALYSIS',
      icon: <UsersRoundIcon size={26} />,
      helpText: helpTextMap[JOB_OPENING_TYPE.APPLICANT_ANALYSIS],
    },
    {
      title: JOB_OPENING_TYPE.AUTO_SUITABILITY,
      label: 'AUTO-SUITABILITY',
      icon: <CircleCheckIcon size={26} />,
      helpText: helpTextMap[JOB_OPENING_TYPE.AUTO_SUITABILITY],
    },
    {
      title: JOB_OPENING_TYPE.AUTO_OUTSTANDING,
      label: 'AUTO-OUTSTANDING',
      icon: <SparklesIcon size={26} />,
      helpText: helpTextMap[JOB_OPENING_TYPE.AUTO_OUTSTANDING],
    },
    {
      title: JOB_OPENING_TYPE.AUTO_ASSESMENTS,
      label: 'AUTO-ASSESSMENTS',
      icon: <ClipboardCheckIcon size={26} />,
      helpText: helpTextMap[JOB_OPENING_TYPE.AUTO_ASSESMENTS],
    },
    {
      title: JOB_OPENING_TYPE.SMART_INTERVIEWS,
      label: 'SMART INTERVIEWS',
      icon: <BrainIcon size={26} />,
      helpText: helpTextMap[JOB_OPENING_TYPE.SMART_INTERVIEWS],
    },
    {
      title: JOB_OPENING_TYPE.PSYCHOMETRIC,
      label: 'PSYCHOMETRIC',
      icon: <BrainIcon size={26} />,
      helpText: helpTextMap[JOB_OPENING_TYPE.PSYCHOMETRIC],
    },
    {
      title: JOB_OPENING_TYPE.WELLNESS,
      label: 'WELLNESS',
      icon: <HeartHandshakeIcon size={26} />,
      helpText: helpTextMap[JOB_OPENING_TYPE.WELLNESS],
    },
  ];

  const isLicenseActive = (feature?: {
    startDate: number | string;
    endDate: number | string;
  }): boolean => {
    if (!feature) return false;
    return Number(feature.endDate) * 1000 > Date.now();
  };

  const getEnabledJobOpeningTypes = (
    subs: SubscriptionDetails | undefined,
  ): Set<JOB_OPENING_TYPE> => {
    const enabled = new Set<JOB_OPENING_TYPE>();
    if (!subs) return enabled;
    if (isLicenseActive(subs.hierTalentBase))
      enabled.add(JOB_OPENING_TYPE.APPLICANT_ANALYSIS);
    if (isLicenseActive(subs.hierTalentAutoClassify))
      enabled.add(JOB_OPENING_TYPE.AUTO_SUITABILITY);
    if (isLicenseActive(subs.hierTalentAutoClassify))
      enabled.add(JOB_OPENING_TYPE.AUTO_OUTSTANDING);
    if (isLicenseActive(subs.hierTalentJobPost))
      enabled.add(JOB_OPENING_TYPE.JOB_POSTING);
    if (isLicenseActive(subs.hierTalentAutoAssessments))
      enabled.add(JOB_OPENING_TYPE.AUTO_ASSESMENTS);
    if (isLicenseActive(subs.hierTalentSmartInterviews))
      enabled.add(JOB_OPENING_TYPE.SMART_INTERVIEWS);
    if (isLicenseActive(subs.hierTalentPsychometricFaxtor))
      enabled.add(JOB_OPENING_TYPE.PSYCHOMETRIC);
    if (isLicenseActive(subs.hierTalentWellness))
      enabled.add(JOB_OPENING_TYPE.WELLNESS);
    return enabled;
  };

  const enabledTypes = getEnabledJobOpeningTypes(subscriptions);
  const visibleElements = allElements.filter(({ title }) =>
    enabledTypes.has(title),
  );

  const alwaysHidden = [JOB_OPENING_TYPE.JOB_OPENING_DETAILS];
  const disabled = [
    JOB_OPENING_TYPE.JOB_OPENING_DETAILS,
    JOB_OPENING_TYPE.APPLICANT_ANALYSIS,
  ];
  const defaultSelected = [
    JOB_OPENING_TYPE.JOB_OPENING_DETAILS,
    JOB_OPENING_TYPE.APPLICANT_ANALYSIS,
  ];

  const [selectedTypes, setSelectedTypes] = useState<JOB_OPENING_TYPE[]>([
    ...defaultSelected,
  ]);
  const [loading, setLoading] = useState(false);

  const selectableElements = visibleElements.filter(
    (item) => !disabled.includes(item.title),
  );
  const allSelected =
    selectableElements.length > 0 &&
    selectableElements.every((item) => selectedTypes.includes(item.title));

  const handleToggle = (type: JOB_OPENING_TYPE) => {
    if (disabled.includes(type)) return;
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    );
  };

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedTypes([...defaultSelected]);
    } else {
      const allSelectable = selectableElements.map((item) => item.title);
      setSelectedTypes([...disabled, ...allSelectable]);
    }
  };

  const handleCreate = async () => {
    setLoading(true);
    try {
      const response = await createJobOpening(selectedTypes);
      const responseData = await response.json();
      if (response.ok) {
        history.push(`/job-openings/${responseData.id}`);
        setJobOpeningElements([]);
        setCompletedJobOpeningDetails([]);
        if (onSuccessClose) onSuccessClose();
        else onClose();
      } else {
        toast.error('Error Creating Job Opening', {
          position: 'bottom-center',
        });
      }
    } catch (error) {
      toast.error('Error Creating Job Opening', {
        position: 'bottom-center',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 select-none">
          {/* Backdrop Blur */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 bg-slate-900/30 backdrop-blur-sm"
          />

          {/* Modal Container (Light Theme) */}
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{
              opacity: 0,
              scale: 0.94,
              y: 8,
              transition: { duration: 0.15 },
            }}
            transition={SPRING}
            className="relative w-full max-w-xl bg-white border border-slate-200/90 rounded-3xl p-6 sm:p-7 text-slate-800 shadow-[0_25px_60px_-15px_rgba(15,23,42,0.12),0_0_1px_1px_rgba(15,23,42,0.05)] backdrop-blur-xl overflow-hidden z-10"
          >
            {/* Subtle light ambient glow */}
            <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 w-64 h-64 bg-purple-100/50 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-0 translate-y-1/2 -translate-x-1/2 w-64 h-64 bg-cyan-100/40 rounded-full blur-3xl pointer-events-none" />

            {/* Header: Title & Close Button */}
            <div className="flex items-center justify-between pb-4 border-b border-slate-100">
              <div className="flex items-center gap-2.5">
                <span className="w-2 h-2 rounded-full bg-purple-600 shadow-[0_0_8px_rgba(168,85,247,0.5)] animate-pulse" />
                <h2 className="text-xs font-mono font-bold tracking-[0.25em] text-purple-700 uppercase">
                  CREATE NEW
                </h2>
              </div>

              <button
                type="button"
                onClick={onClose}
                className="group p-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-800 transition-colors border border-slate-200/80 cursor-pointer"
                aria-label="Close dialog"
              >
                <XIcon size={16} />
              </button>
            </div>

            {/* Sub-bar: Selection stats & Select All */}
            <div className="flex items-center justify-between py-3.5 px-1">
              <button
                type="button"
                onClick={handleSelectAll}
                className="flex items-center gap-2.5 group cursor-pointer"
              >
                <div
                  className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${
                    allSelected
                      ? 'bg-purple-600 border-purple-600 text-white shadow-xs'
                      : 'bg-white border-slate-300 group-hover:border-purple-500'
                  }`}
                >
                  {allSelected && (
                    <CheckIcon size={12} className="stroke-[3]" />
                  )}
                </div>
                <span className="text-xs font-mono font-semibold text-slate-700 group-hover:text-purple-700 transition-colors">
                  {t(ADMIN_PAGES_KEYS.jobOpenings.createDialog.selectAll)}
                </span>
              </button>

              <span className="text-[11px] font-mono font-semibold px-2.5 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200">
                {selectedTypes.filter((tp) => !alwaysHidden.includes(tp)).length} /{' '}
                {visibleElements.filter((i) => !alwaysHidden.includes(i.title)).length}{' '}
                SELECTED
              </span>
            </div>

            {/* Grid Matrix (Light Themed) */}
            <div className="grid grid-cols-2 sm:grid-cols-4 border border-slate-200 rounded-2xl overflow-hidden bg-slate-50/50 my-2 shadow-[inset_0_1px_2px_rgba(0,0,0,0.02)]">
              {visibleElements.map((item, idx) => {
                const isSelected = selectedTypes.includes(item.title);
                const isDisabled = disabled.includes(item.title);

                return (
                  <motion.button
                    key={item.title}
                    type="button"
                    onClick={() => handleToggle(item.title)}
                    disabled={isDisabled}
                    whileHover={!isDisabled ? { scale: 1.02 } : {}}
                    whileTap={!isDisabled ? { scale: 0.98 } : {}}
                    title={item.helpText}
                    className={`
                      relative p-4 sm:p-5 flex flex-col items-center justify-center text-center gap-2.5 transition-all duration-200 select-none
                      border-slate-200
                      ${(idx + 1) % 2 === 0 ? 'border-r-0' : 'border-r'}
                      ${(idx + 1) % 4 === 0 ? 'sm:border-r-0' : 'sm:border-r'}
                      ${idx < visibleElements.length - 2 ? 'border-b' : 'border-b-0'}
                      ${idx < 4 && visibleElements.length > 4 ? 'sm:border-b' : 'sm:border-b-0'}
                      ${
                        isSelected
                          ? 'bg-purple-50/80 text-purple-700 hover:bg-purple-100/60'
                          : 'bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                      }
                      ${isDisabled ? 'cursor-default' : 'cursor-pointer'}
                    `}
                  >
                    {/* Active Checkmark Pill in Corner */}
                    {isSelected && (
                      <span className="absolute top-2.5 right-2.5 w-4 h-4 rounded-full bg-purple-600 text-white flex items-center justify-center shadow-xs">
                        <CheckIcon size={10} className="stroke-[3]" />
                      </span>
                    )}

                    {/* Icon */}
                    <div
                      className={`transition-all duration-200 ${
                        isSelected
                          ? 'text-purple-600 drop-shadow-[0_2px_8px_rgba(168,85,247,0.3)] scale-110'
                          : 'text-slate-500 group-hover:text-slate-800'
                      }`}
                    >
                      {item.icon}
                    </div>

                    {/* Uppercase Matrix Label */}
                    <span
                      className={`text-[10.5px] font-mono font-bold tracking-wider uppercase leading-tight ${
                        isSelected
                          ? 'text-purple-700 font-bold'
                          : 'text-slate-600'
                      }`}
                    >
                      {item.label}
                    </span>
                  </motion.button>
                );
              })}
            </div>

            {/* Bottom Actions: Cancel & Create Button */}
            <div className="flex items-center justify-between gap-3 pt-5 mt-2 border-t border-slate-100">
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2.5 rounded-xl text-xs font-semibold font-mono tracking-wider text-slate-600 hover:text-slate-900 bg-slate-100 hover:bg-slate-200/80 border border-slate-200/90 transition-all active:scale-95 cursor-pointer shadow-xs"
              >
                {t(ADMIN_PAGES_KEYS.jobOpenings.createDialog.cancel)}
              </button>

              <button
                type="button"
                onClick={handleCreate}
                disabled={
                  loading ||
                  selectedTypes.filter((tp) => !alwaysHidden.includes(tp))
                    .length === 0
                }
                className="px-6 py-2.5 rounded-xl text-xs font-bold font-mono tracking-wider uppercase text-slate-950 bg-gradient-to-r from-purple-400 via-purple-300 to-cyan-300 hover:from-purple-300 hover:to-cyan-200 shadow-[0_4px_16px_rgba(168,85,247,0.25)] transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none cursor-pointer flex items-center gap-2"
              >
                {loading && (
                  <span className="w-3.5 h-3.5 border-2 border-slate-950 border-t-transparent rounded-full animate-spin" />
                )}
                {loading
                  ? t(ADMIN_PAGES_KEYS.jobOpenings.createDialog.creating)
                  : t(ADMIN_PAGES_KEYS.jobOpenings.createDialog.createButton)}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
